import { useState } from "react";

import { ListFilter } from "lucide-react";

import { useAppForm } from "@/components/Form";

import { GetAllCasesApiV1CasesGetData } from "@api/generated/types.gen";

import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

import { cn } from "@utils/ui";

import { STATUS_MAPPING } from "../constants/status";
import { FilterFormData } from "../schema/filters";

type FilterProps = {
  className?: string;
  onFiltersChange?: (filters: GetAllCasesApiV1CasesGetData["query"]) => void;
};

export const Filter = ({ className, onFiltersChange }: FilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const form = useAppForm({
    defaultValues: {
      template_id: "",
      claim_reference_number: "",
      appointment_date: "",
      examinee_name: "",
      status: undefined,
    } as FilterFormData,
    onSubmit: async ({ value }) => {
      const filterValues: GetAllCasesApiV1CasesGetData["query"] = {
        template_id: value.template_id || undefined,
        claim_number: value.claim_reference_number || undefined,
        appointment_date: value.appointment_date || undefined,
        examinee_name: value.examinee_name || undefined,
        status: value.status || undefined,
      };

      onFiltersChange?.(filterValues);

      // Check if any filters are applied
      const activeFilters = Object.values(value).some(
        (val) => val && val.trim() !== ""
      );

      setHasActiveFilters(activeFilters);

      setIsOpen(false);
    },
  });

  const statusOptions = Object.entries(STATUS_MAPPING).map(([value, label]) => ({
    value,
    label,
  }));

  const handleCancel = () => {
    form.reset();
    onFiltersChange?.({});
    setHasActiveFilters(false);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {/* Gray simple-outline by default (matches Cancel); switches to the
              blue secondary-subtle variant once filters are active — a real
              variant swap, not a className color override. */}
          <Button
            variant={hasActiveFilters ? "secondary-subtle" : "simple-outline"}
            size="sm"
            className={cn("h-10 gap-2 transition-all duration-150", className)}
          >
            <ListFilter className="size-4" />
            Filter
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="flex max-h-[var(--radix-popover-content-available-height)] w-96 flex-col overflow-hidden rounded-xl border border-border bg-background-white p-0 shadow-lg"
          align="end"
          side="bottom"
          sideOffset={8}
        >
          <h3 className="flex-shrink-0 border-b border-border px-4 pb-2 pt-4 text-sm font-semibold">
            Filter
          </h3>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-2">
              <form.AppField name="claim_reference_number">
                {(field) => (
                  <field.InputField
                    label="Claim reference number"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Enter claim reference number"
                  />
                )}
              </form.AppField>

              <form.AppField name="appointment_date">
                {(field) => (
                  <field.DateField
                    label="Appointment date"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Select date"
                    dateFormat="yyyy-MM-dd"
                  />
                )}
              </form.AppField>

              <form.AppField name="examinee_name">
                {(field) => (
                  <field.InputField
                    label="Examinee name"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Enter examinee name"
                  />
                )}
              </form.AppField>

              <form.AppField name="status">
                {(field) => (
                  <field.SelectField
                    label="Report status"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Select a status"
                    options={statusOptions}
                  />
                )}
              </form.AppField>
            </div>

            <form.AppForm>
              <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-border px-4 py-4">
                <button
                  type="button"
                  className="font-head text-xs font-semibold text-foreground-muted underline-offset-2 hover:text-foreground hover:underline"
                  onClick={handleCancel}
                >
                  Clear all
                </button>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="simple-outline"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <form.SubmitButton size="sm">Apply filters</form.SubmitButton>
                </div>
              </div>
            </form.AppForm>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
};
